import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { Conclave, ConclaveApiService } from '../conclave-api.service';
import { FilterBaseAccessService } from '../../../auth/filterbase-access';

@Component({
  selector: 'app-conclave-list',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule],
  templateUrl: './conclave-list.component.html',
  styleUrl: './conclave-list.component.css'
})
export class ConclaveListComponent {
  // Used by template filter
  year: number | string | null = new Date().getFullYear();
  years: number[] = [];

  activeTab: 'past' | 'upcoming' = 'upcoming';

  isLoading = false;
  errorMessage: string | null = null;

  conclaves: Conclave[] = [];
  conclave_page : boolean=false;
  create_conclave : boolean=false;
  conclave_list : Boolean=false;

  get filteredConclaves(): Conclave[] {
    const now = new Date();

    if (this.activeTab === 'past') {
      return (this.conclaves ?? []).filter((c) => this.isPastConclave(c, now));
    }

    return (this.conclaves ?? []).filter((c) => this.isUpcomingConclave(c, now));
  }

  constructor(
    access: FilterBaseAccessService,
    private readonly route: ActivatedRoute,
    private readonly conclaveApi: ConclaveApiService
  ) {
    const currentYear = new Date().getFullYear();
    this.years = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1, currentYear + 2];
        this.conclave_page=access.checkaccessforfiltersandactions("conclave");
        this.create_conclave = access.checkaccessforfiltersandactions('create conclave');
        this.conclave_list = access.checkaccessforfiltersandactions('conclave list');

  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params) => {
      const yearParam = params.get('year');
      this.year = yearParam ? yearParam : this.year;
      this.loadConclaves();
    });
  }

  getMonthName(date: string | undefined): string {
    if (!date) return '';

    const [, month] = date.split('T')[0].split('-');
    return new Date(0, Number(month) - 1, 1).toLocaleString('en-US', {
      month: 'short'
    });
  }

  getDay(date: string | undefined): string {
    if (!date) return '';

    return date.split('T')[0].split('-')[2];
  }

  onTabChange(tab: 'past' | 'upcoming'): void {
    this.activeTab = tab;
  }

  private toDateTime(dateStr?: string, timeStr?: string): Date | null {
    if (!dateStr) return null;

    const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;

    // If time is missing, treat as start of day.
    const timePart = timeStr ? timeStr : '00:00:00';

    // Accept both 'HH:mm' and 'HH:mm:ss'
    const normalizedTime = timePart.length === 5 ? `${timePart}:00` : timePart;

    const dt = new Date(`${datePart}T${normalizedTime}`);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  // Upcoming rule: show conclaves that start in the future OR start today (regardless of time)
  private isUpcomingConclave(conclave: Conclave, now: Date): boolean {
    const start = this.toDateTime(conclave.start_date, conclave.start_time);
    if (start && start.getTime() >= now.getTime()) return true;

    return this.isToday(conclave, now);
  }

  // Past rule: end datetime strictly in the past
  private isPastConclave(conclave: Conclave, now: Date): boolean {
    const end = this.toDateTime(conclave.end_date, conclave.end_time);
    if (!end) return false;
    return end.getTime() < now.getTime();
  }

  private isToday(conclave: Conclave, now: Date): boolean {
    const start = this.toDateTime(conclave.start_date, conclave.start_time);
    if (!start) return false;

    return (
      start.getFullYear() === now.getFullYear() &&
      start.getMonth() === now.getMonth() &&
      start.getDate() === now.getDate()
    );
  }

  onYearChange(): void {
    this.loadConclaves();
  }

  private loadConclaves(): void {
    if (!this.year) {
      this.conclaves = [];
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;

    this.conclaveApi
      .fetchConclavesByYear(this.year)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (res) => {
          this.conclaves = res?.data ?? [];
        },
        error: (err) => {
          console.error('Conclave list API error:', err);
          this.conclaves = [];
          this.errorMessage = 'Failed to load conclaves.';
        }
      });
  }
}

